import type { ReactElement } from 'react'
import type { FilterGroup } from 'src/utils/common'
import React, { useState, useEffect, useMemo, useContext } from 'react'
import { reaction, toJS } from 'mobx'
import { useLocalObservable, observer } from 'mobx-react'
import { FormContext, ExpandedRemark } from 'src/components'
import { appStore, pageStore } from 'src/stores'
import { $api } from 'src/plugins'
import { getFormPermission } from './helper'
import { initFilter } from 'src/utils/common'

type FormData = {
  // 当前表单父表单配置的 formId（比如说树表单，组合表单）
  parentFormId?: string,
  // 查询参数
  filterGroup?: FilterGroup,
}

type Props = {
  formCode: string,
  formData?: FormData,
}

const CustomPropsContext = React.createContext<Props>({ formCode: '' })
const CustomContext = React.createContext<any>(undefined)

const CustomRemark = observer(() => {
  const custom = useContext(CustomContext)
  const { Nav_Form } = custom.pageConfig
  return <ExpandedRemark expanded={Nav_Form?.REMARK_EXPAND} remark={Nav_Form?.REMARK} />
})

const CustomButton = observer((props: {
  btnCode: string,
  render?: (btn: any) => ReactElement
}) => {
  const custom = useContext(CustomContext)
  const btn = useMemo(() => {
    return toJS(custom.pageBtns.find((btn: any) => btn.CODE === props.btnCode))
  }, [custom.pageBtns, props.btnCode])
  if (!btn) return null
  return props.render?.(btn) ?? <span>{btn.LABEL}</span>
})

const CustomPage = (props: Props) => {
  const form = useContext(FormContext)

  const state = useLocalObservable(() => {
    return {
      // 自定义表单配置
      pageConfig: {} as any,
      setPageConfig (config: any) {
        this.pageConfig = config
      },
      // 自定义表单配置的按钮
      get pageBtns () {
        const initBtns = this.pageConfig.Nav_PageCustom?.Nav_Btns ?? []

        const formPerm = getFormPermission(form.ID)
        if (typeof formPerm === 'boolean') {
          return formPerm ? initBtns : []
        } else {
          const validIds = formPerm.Nav_Btns.map((btn: any) => btn.BtnId)
          return initBtns.filter((btn: any) => validIds.indexOf(btn.ID) !== -1)
        }
      }
    }
  })

  useEffect(() => {
    const { OrgId } = appStore.current.headers
    const payload = initFilter({ OrgId, Keyword: props.formCode })
    $api['app/getCustomPageConfigInfo'](payload).then((res: any) => {
      res && state.setPageConfig(res)
    })
  }, [props.formCode])

  const [componentName, setComponentName] = useState()
  useEffect(() => reaction(
    () => state.pageConfig.Nav_PageCustom?.COMPONENT_NAME,
    name => name && setComponentName(name)
  ), [])

  const pages = pageStore.customPages
  const Page = componentName && pages[componentName] ? pages[componentName] : () => null
  return (
    <CustomPropsContext.Provider value={props}>
      <CustomContext.Provider value={state}>
        <CustomRemark />
        <Page />
      </CustomContext.Provider>
    </CustomPropsContext.Provider>
  )
}
CustomPage.CustomButton = CustomButton

export { CustomPropsContext, CustomContext }
export default CustomPage